import React, { useEffect, useRef, useState } from "react";
import s from "./style.module.css";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import { scaleLinear, scaleOrdinal } from "d3-scale";
import { range } from "d3-array";
import { regressionLinear } from "d3-regression";
import spi from "../../data/SPI_index.json";

const margin = {
  top: 20,
  right: 50,
  bottom: 100,
  left: 100,
};

const pathString =
  range(6)
    .map((d) => [
      Math.cos((d / 6) * Math.PI * 2) * 5,
      Math.sin((d / 6) * Math.PI * 2) * 5,
    ])
    .reduce(
      (acc, cur, i) => acc + `${i === 0 ? "M" : "L"}${cur[0]},${cur[1]} `,
      ""
    ) + "Z";

const CorrelationChart = ({
  data,
  title,
  note,
  source,
  spi_index,
  reverse,
  jitter,
  xAxisLabel,
  yAxisLabel,
  maxValue = 100,
  minValue = 0,
  addValue,
  countries,
}) => {
  const { ref, width, windowSize, inView } = useVizualisation();

  const other = require(`./../../data/${data}.json`);

  const height = 500;

  const w = 950 - margin.left - margin.right;
  const h = height - margin.top - margin.bottom;

  const [hoverCountry, setHoverCountry] = useState(undefined);

  const x = scaleLinear()
    .domain(
      reverse === "true" ? [+maxValue, +minValue] : [+minValue, +maxValue]
    )
    .range([0, w]);

  const y = scaleLinear().range([h, 0]).domain([0, 100]);

  let scores = useRef([]);

  const r = regressionLinear()
    .x((d) => d.value)
    .y((d) => d.spi)
    .domain(x.domain());

  const c3 = scaleOrdinal()
    .domain(["age_stat_laws", "nso", "wpfi"])
    .range(["#3574B6", "#3574B6", "#3574B6", "#0c5860"]);

  useEffect(() => {
    const spiCountries = spi
      .filter((d) => d["SPI.INDEX"] !== "NA")
      .map((d) => d.country);

    const countryIsoMap = spi
      .filter((d) => d["SPI.INDEX"] !== "NA")
      .reduce((acc, cur) => {
        acc.set(cur.country, cur.iso3c);
        return acc;
      }, new Map());

    scores.current = other
      .filter((d) => spiCountries.includes(d.country))
      .map((d) => {
        return {
          ...d,
          x: x(jitter === "true" ? d.jittered : d.value),
          y: y(spi.find((s) => s.country === d.country)["SPI.INDEX"]),
          spi: spi.find((s) => s.country === d.country)["SPI.INDEX"],
          income_group: spi.find((s) => s.country === d.country).income,
          countryLabel: countries[countryIsoMap.get(d.country)],
        };
      });
  });

  return (
    <Vizualisation
      title={title}
      note={note}
      source={source}
      ref={ref}
      size="large"
    >
      <svg viewBox="0 0 950 500">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop
              offset="0%"
              style={{ stopColor: "#CBD8D9", stopOpacity: 1 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "#B0CBCD", stopOpacity: 1 }}
            />
          </linearGradient>
        </defs>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <rect
            x={-40}
            width={Math.max(0, w + 80)} // generates an error (negative value) on initial load?
            height={h}
            fill="url(#gradient)"
            opacity={0.6}
          />
          {x.ticks((+maxValue - +minValue) / 10).map((d) => (
            <line
              key={`line-${d}`}
              y2={h}
              x1={x(d)}
              x2={x(d)}
              stroke="white"
              opacity={0.7}
            />
          ))}
          <line
            x1={x(+minValue)}
            y1={y(r(scores.current).predict(+minValue))}
            x2={x(+maxValue)}
            y2={y(r(scores.current).predict(+maxValue))}
            stroke="black"
            style={{
              strokeDasharray: "2 2",
            }}
          />
          {y
            .nice()
            .ticks(10)
            .map((d) => (
              <text
                key={`y-tick-${d}`}
                y={y(d)}
                x={-60}
                className={s.tickLabel}
                style={{ fontSize: width < 600 ? 18 : 12 }}
              >
                {d}
              </text>
            ))}
          {x
            .nice()
            .ticks(+maxValue / 10)
            .map((d) => (
              <text
                key={`x-tick-${d}`}
                x={x(d)}
                y={h + 30}
                className={s.tickLabel}
                style={{ fontSize: width < 600 ? 18 : 12 }}
              >
                {(reverse === "true" ? 100 - d : d) + +addValue}
              </text>
            ))}
          {scores.current.map((d, i) => (
            <g
              key={`value-${i}`}
              transform={`translate(${d.x}, ${d.y})`}
              onMouseOver={() => setHoverCountry(d)}
              onMouseOut={() => setHoverCountry(undefined)}
            >
              <circle
                r={12}
                fill="transparent"
                style={{
                  strokeWidth: 2,
                }}
              />
              <path
                d={pathString}
                stroke={
                  hoverCountry && hoverCountry.country === d.country
                    ? "#F2C118"
                    : c3(data)
                }
                fill="white"
              />
              <circle
                r={3}
                fill={
                  hoverCountry && hoverCountry.country === d.country
                    ? "#F2C118"
                    : c3(data)
                }
              />
            </g>
          ))}
          {hoverCountry && (
            <g
              transform={`translate(${hoverCountry.x}, ${hoverCountry.y})`}
              className={s.countryHighlight}
            >
              <circle r={8} />
              <line y1={-8} y2={-40} />
              <text
                dy={-45}
                fill="white"
                stroke="white"
                style={{
                  strokeWidth: 2,
                  textAnchor: "middle",
                  fontSize: width < 600 ? 24 : 14,
                }}
              >
                {hoverCountry.countryLabel}
              </text>
              <text
                style={{
                  textAnchor: "middle",
                  fontSize: width < 600 ? 24 : 14,
                }}
                dy={-45}
              >
                {hoverCountry.countryLabel}
              </text>
            </g>
          )}
          <text
            x={w / 2}
            y={h}
            className={s.xAxisLabel}
            dy={60}
            style={{ fontSize: width < 600 ? 24 : 14 }}
          >
            {xAxisLabel}
          </text>
          <text
            transform={`translate(0, ${h / 2}) rotate(-90)`}
            dy={-90}
            style={{ fontSize: width < 600 ? 24 : 14 }}
            className={s.yAxisLabel}
          >
            {yAxisLabel}
          </text>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default CorrelationChart;
